import { useEffect, useRef, useState } from 'react';
import ValidatedInput from '../../validations/ValidatedInput';
import { testimonialApi } from './api';
import './TestimonialsScreen.css';

function FileDrop({ file, preview, onChange }) {
  const inputRef = useRef(null);
  return (
    <button type="button" className={`tm-drop${file || preview ? ' is-filled' : ''}`} onClick={() => inputRef.current?.click()}>
      <span className="tm-drop__label">Product image</span>
      {preview ? (
        <span className="tm-drop__preview"><img src={preview} alt="" /></span>
      ) : (
        <span className="tm-drop__icon" aria-hidden>↑</span>
      )}
      <span className="tm-drop__meta">{file?.name || 'Piece gifted / worn (not a profile photo)'}</span>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </button>
  );
}

export default function TestimonialsScreen({ onMessage }) {
  const flash = (t) => onMessage?.(t);
  const [testimonials, setTestimonials] = useState([]);
  const [testFile, setTestFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testForm, setTestForm] = useState({ name: '', role: 'Customer', message: '', rating: 5 });

  const refresh = async () => {
    const t = await testimonialApi.adminAll().catch(() => ({ data: { data: [] } }));
    setTestimonials(t.data.data || []);
  };

  useEffect(() => { refresh().catch(() => flash('Could not load testimonials')); }, []);

  useEffect(() => {
    if (testFile) {
      const url = URL.createObjectURL(testFile);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (editingId) {
      const item = testimonials.find((t) => t._id === editingId);
      setPreview(item?.image || '');
      return undefined;
    }
    setPreview('');
    return undefined;
  }, [testFile, editingId, testimonials]);

  const resetForm = () => {
    setTestForm({ name: '', role: 'Customer', message: '', rating: 5 });
    setTestFile(null);
    setEditingId(null);
  };

  const saveTestimonial = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(testForm).forEach(([k, v]) => fd.append(k, v));
      if (testFile) fd.append('image', testFile);
      if (editingId) {
        await testimonialApi.update(editingId, fd);
        flash('Testimonial updated');
      } else {
        await testimonialApi.create(fd);
        flash('Testimonial added');
      }
      resetForm();
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Testimonial failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t._id);
    setTestForm({
      name: t.name || '',
      role: t.role || 'Customer',
      message: t.message || '',
      rating: t.rating ?? 5,
    });
    setTestFile(null);
  };

  return (
    <div className="tm-screen">
      <form className="tm-screen__panel" onSubmit={saveTestimonial}>
        <p className="tm-screen__label">{editingId ? 'Edit testimonial' : 'New testimonial'}</p>
        <label className="tm-field"><span>Name</span>
          <ValidatedInput kind="text" name="name" value={testForm.name} onChange={(e) => setTestForm((f) => ({ ...f, name: e.target.value }))} required />
        </label>
        <label className="tm-field"><span>Role</span>
          <ValidatedInput kind="text" name="role" value={testForm.role} onChange={(e) => setTestForm((f) => ({ ...f, role: e.target.value }))} />
        </label>
        <label className="tm-field"><span>Message</span>
          <ValidatedInput kind="textarea" name="message" rows={4} value={testForm.message} onChange={(e) => setTestForm((f) => ({ ...f, message: e.target.value }))} required />
        </label>
        <label className="tm-field"><span>Rating</span>
          <ValidatedInput kind="number" name="rating" value={testForm.rating} onChange={(e) => setTestForm((f) => ({ ...f, rating: e.target.value }))} />
        </label>
        <FileDrop file={testFile} preview={preview} onChange={setTestFile} />
        <div className="tm-form-actions">
          {editingId && (
            <button type="button" className="tm-cancel" onClick={resetForm}>Cancel</button>
          )}
          <button type="submit" className="tm-save" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update testimonial' : 'Add testimonial'}
          </button>
        </div>
      </form>

      <aside className="tm-screen__aside">
        <div className="tm-list-head"><p className="tm-screen__label">Published quotes</p><span>{testimonials.length}</span></div>
        <div className="tm-list">
          {testimonials.length === 0 && <p className="tm-empty">No testimonials yet.</p>}
          {testimonials.map((t) => (
            <article key={t._id} className={`tm-card${editingId === t._id ? ' is-editing' : ''}`}>
              <div className="tm-card__top">
                {t.image ? <img src={t.image} alt="" className="tm-card__avatar" /> : <div className="tm-card__avatar tm-card__avatar--empty" />}
                <div>
                  <strong>{t.name}</strong>
                  <span>{t.role || 'Customer'} · {t.rating || 5}★</span>
                </div>
              </div>
              <p>{t.message}</p>
              <div className="tm-card__actions">
                <button type="button" className="tm-card__edit" onClick={() => startEdit(t)}>Edit</button>
                <button type="button" className="tm-card__remove" onClick={async () => { await testimonialApi.remove(t._id); if (editingId === t._id) resetForm(); refresh(); }}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}
